import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDrop } from 'react-dnd';
import { Eye, Compass, Brain, Check } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import ModuleTile from '../components/ModuleTile';
import LearnerRow from '../components/LearnerRow';
import GroupContainer from '../components/GroupContainer';

const ProgramBuilder = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([
    {
      id: 1,
      name: '',
      learners: [
        { id: 1, name: '', modules: [] },
        { id: 2, name: '', modules: [] },
        { id: 3, name: '', modules: [] },
        { id: 4, name: '', modules: [] }
      ]
    }
  ]);
  const [modules, setModules] = useState([]);
  const [currentStep] = useState(1);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/modules`);
        const data = await response.json();
        setModules(data.modules);
      } catch (error) {
        console.error('Erreur chargement modules:', error);
      }
    };
    
    // Charger les données existantes du programme si elles existent
    const savedProgram = localStorage.getItem('simulator_program');
    if (savedProgram) {
      try {
        const programData = JSON.parse(savedProgram);
        // Reconstruire la structure de groupes pour l'édition
        const reconstructedGroups = programData.groups.map((group, groupIndex) => {
          const baseId = (groupIndex + 1) * 10;
          return {
            id: groupIndex + 1,
            name: group.name,
            learners: [
              ...group.learners.map((learner, learnerIndex) => ({
                id: baseId + learnerIndex + 1,
                name: learner.name,
                modules: learner.modules
              })),
              // Compléter avec des apprenants vides pour atteindre 4
              ...Array.from({ length: Math.max(0, 4 - group.learners.length) }, (_, i) => ({
                id: baseId + group.learners.length + i + 1,
                name: '',
                modules: []
              }))
            ]
          };
        });
        setGroups(reconstructedGroups);
      } catch (error) {
        console.error('Erreur lors du chargement du programme:', error);
      }
    }
    
    fetchModules();
  }, []);

  const addGroup = () => {
    const newGroupId = groups.length + 1;
    const newGroup = {
      id: newGroupId,
      name: '',
      learners: [
        { id: newGroupId * 10 + 1, name: '', modules: [] },
        { id: newGroupId * 10 + 2, name: '', modules: [] },
        { id: newGroupId * 10 + 3, name: '', modules: [] },
        { id: newGroupId * 10 + 4, name: '', modules: [] }
      ]
    };
    setGroups(prevGroups => [...prevGroups, newGroup]);
  };

  const updateGroupName = (groupId, newName) => {
    setGroups(prevGroups => 
      prevGroups.map(group => 
        group.id === groupId ? { ...group, name: newName } : group
      )
    );
  };

  const updateLearnerName = (groupId, learnerId, newName) => {
    setGroups(prevGroups => 
      prevGroups.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            learners: group.learners.map(learner =>
              learner.id === learnerId ? { ...learner, name: newName } : learner
            )
          };
        }
        return group;
      })
    );
  };

  const addModuleToLearner = (groupId, learnerId, module) => {
    setGroups(prevGroups => 
      prevGroups.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            learners: group.learners.map(learner => {
              if (learner.id === learnerId) {
                // Vérifier si le module n'est pas déjà présent pour CET apprenant
                const moduleExists = learner.modules.find(m => m.module_id === module.id);
                if (moduleExists) {
                  // Ne pas ajouter, mais ne pas afficher d'alert
                  console.log(`Module ${module.name} déjà présent pour ${learner.name || 'cet apprenant'}`);
                  return learner;
                }
                
                // Vérifier la limite de 10 modules pour CET apprenant
                if (learner.modules.length >= 10) {
                  // Ne pas ajouter, mais ne pas afficher d'alert
                  console.log(`Limite de 10 modules atteinte pour ${learner.name || 'cet apprenant'}`);
                  return learner;
                }
                
                const newModule = {
                  module_id: module.id,
                  module_name: module.name
                };
                
                // Trier les modules par ID après ajout
                const updatedModules = [...learner.modules, newModule].sort((a, b) => a.module_id - b.module_id);
                
                return { ...learner, modules: updatedModules };
              }
              return learner;
            })
          };
        }
        return group;
      })
    );
  };

  const removeModuleFromLearner = (groupId, learnerId, moduleId) => {
    setGroups(prevGroups => 
      prevGroups.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            learners: group.learners.map(learner => {
              if (learner.id === learnerId) {
                return {
                  ...learner,
                  modules: learner.modules.filter(m => m.module_id !== moduleId)
                };
              }
              return learner;
            })
          };
        }
        return group;
      })
    );
  };

  const validateProgram = () => {
    // Vérifier que tous les apprenants avec des modules ont un nom
    const hasUnnamedLearners = groups.some(group => 
      group.learners.some(learner => 
        learner.modules.length > 0 && !learner.name.trim()
      )
    );

    if (hasUnnamedLearners) {
      // Ne pas utiliser alert, mais marquer visuellement les champs requis
      console.log('Des apprenants avec modules n\'ont pas de nom');
      return;
    }

    // Vérifier qu'il y a au moins un apprenant avec des modules
    const hasAnyValidLearners = groups.some(group => 
      group.learners.some(learner => 
        learner.modules.length > 0 && learner.name.trim()
      )
    );

    if (!hasAnyValidLearners) {
      console.log('Aucun apprenant valide avec modules');
      return;
    }

    // Sauvegarder le programme dans le localStorage
    const programData = {
      groups: groups.map((group, index) => ({
        name: group.name.trim() || `Groupe ${index + 1}`,
        learners: group.learners.filter(learner => learner.name.trim() && learner.modules.length > 0).map(learner => ({
          name: learner.name,
          modules: learner.modules
        }))
      })).filter(group => group.learners.length > 0)
    };

    localStorage.setItem('simulator_program', JSON.stringify(programData));
    navigate('/options');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Titre principal */}
      <div className="bg-[#fff5ee] py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-center mb-8">Construisez votre programme de formation</h1>
          <Breadcrumb currentStep={currentStep} />
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-3 gap-4">
          {/* Grille des groupes (2/3) */}
          <div className="col-span-2">
            {groups.map(group => (
              <GroupContainer
                key={group.id}
                group={group}
                modules={modules}
                onUpdateGroupName={updateGroupName}
                onUpdateLearnerName={updateLearnerName}
                onAddModule={addModuleToLearner}
                onRemoveModule={removeModuleFromLearner}
              />
            ))}
            
            <button 
              onClick={addGroup}
              className="add-group-btn"
              data-testid="add-group-button"
            >
              +
            </button>
            
            {/* Texte explicatif sous les groupes */}
            <div style={{ 
              marginTop: '20px', 
              fontSize: '13px', 
              color: '#999999',
              lineHeight: '1.5'
            }}>
              Créez un ou plusieurs groupes de formations, indiquez les personnes que vous souhaitez former et déposez les modules qui correspondent à leur besoin. En cas de doute, nous retravaillerons le programme ensemble.
            </div>
          </div>

          {/* Modules disponibles (1/3) */}
          <div className="col-span-1">
            <div className="grid grid-cols-2 gap-2 sticky top-4">
              {modules.map(module => (
                <ModuleTile
                  key={module.id}
                  module={module}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Bouton validation */}
        <div className="text-center mt-12">
          <button
            onClick={validateProgram}
            className="btn-primary"
            data-testid="validate-program-button"
          >
            Valider le programme
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgramBuilder;